import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Check, Sparkles, Loader2, Crown } from 'lucide-react';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const packages = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      period: 'forever',
      features: [
        '1 Resume',
        '5 Job Applications',
        'Basic AI Suggestions',
        'Content Repurposer (3/month)',
      ],
      cta: 'Current Plan',
      popular: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 19.99,
      period: '/month',
      features: [
        'Unlimited Resumes',
        'Unlimited Applications',
        'Advanced AI Optimization',
        'Unlimited Content Repurposing',
        'Priority Support',
        'Resume Templates',
      ],
      cta: 'Upgrade to Pro',
      popular: true,
    },
    {
      id: 'lifetime',
      name: 'Lifetime',
      price: 99.99,
      period: 'one-time',
      features: [
        'Everything in Pro',
        'Lifetime Access',
        'All Future Features',
        'VIP Support',
        'Early Access to New Tools',
      ],
      cta: 'Get Lifetime Access',
      popular: false,
    },
  ];

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    setCheckingPayment(true);
    
    try {
      const token = localStorage.getItem('careerboost_token');
      const response = await axios.get(
        `${BACKEND_URL}/api/payments/status/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! Your account has been upgraded.');
        navigate('/dashboard');
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.debug('Payment check error:', error);
      toast.error('Error verifying payment. Please contact support.');
      setCheckingPayment(false);
    }
  };

  const handleUpgrade = async (packageId) => {
    if (packageId === 'free') return;
    
    if (!user) {
      navigate('/register');
      return;
    }

    setLoading(packageId);

    try {
      const token = localStorage.getItem('careerboost_token');
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${BACKEND_URL}/api/payments/checkout`,
        { package_id: packageId, origin_url: originUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.debug('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setLoading(null);
    }
  };

  if (checkingPayment) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mb-4" />
          <h2 className="text-xl font-semibold text-slate-900">Verifying your payment...</h2>
          <p className="text-slate-600 mt-2">Please wait while we confirm your upgrade.</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="pricing-page">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <h1 className="text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Upgrade Your Career
          </h1>
          <p className="text-lg text-slate-600">
            Choose the plan that fits your needs and supercharge your job search
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`relative ${pkg.popular ? 'border-indigo-500 border-2 shadow-xl' : 'border-slate-200'}`}
              data-testid={`pricing-card-${pkg.id}`}
            >
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 px-4 py-1 rounded-full bg-indigo-600 text-white text-sm font-medium">
                    <Crown className="w-4 h-4" />
                    Most Popular
                  </span>
                </div>
              )}
              <CardHeader className="text-center pb-4">
                <CardTitle className="text-xl" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {pkg.name}
                </CardTitle>
                <div className="mt-4">
                  <span className="text-4xl font-bold text-slate-900">
                    ${pkg.price}
                  </span>
                  <span className="text-slate-500 ml-1">{pkg.period}</span>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <ul className="space-y-3">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span className="text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleUpgrade(pkg.id)}
                  disabled={loading === pkg.id || pkg.id === 'free'}
                  className={`w-full ${
                    pkg.popular
                      ? 'bg-indigo-600 hover:bg-indigo-700'
                      : pkg.id === 'free'
                      ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                      : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                  data-testid={`upgrade-btn-${pkg.id}`}
                >
                  {loading === pkg.id ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    pkg.cta
                  )}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Trust Section */}
        <div className="text-center pt-8 border-t border-slate-200 mt-12">
          <p className="text-slate-500 text-sm">
            Secure payment powered by Stripe • Cancel anytime • 30-day money-back guarantee
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Pricing;
